/**
 * PreviaWhatsApp — cómo ve el mensaje quien lo recibe.
 *
 * Copia adaptada de `modules/whatsapp/components/WhatsAppPreview.tsx` (el mockup del teléfono y
 * `renderWhatsAppMarkdown`). No se reusa aquel componente: su prop exige el tipo legacy y
 * sustituye variables por su cuenta. Acá el texto llega YA compuesto (cuerpo + pie) y con las
 * variables resueltas por el backend o por la previa local: la previa no arma nada, sólo muestra.
 * Así la previa y el envío no pueden divergir.
 *
 * `whatsapp/card` (D-A): el pie NO va dentro del texto sino aparte (`pie`), y puede haber un
 * encabezado de texto (`encabezadoTexto`) o de imagen (`mediaUrl`).
 */

import { useState } from "react";
import type { ReactNode } from "react";
import { MessageCircle, Phone, Video, MoreVertical, ExternalLink, PhoneCall, Reply, ImageOff, Loader2 } from "lucide-react";
import type { BotonPlantilla } from "../types";
import { activaApi } from "../../campana-nueva/api";

interface Props {
  texto: string;                   // YA compuesto (cuerpo + pie) y con variables resueltas o con ejemplos
  mediaUrl?: string;
  botones: BotonPlantilla[];
  nombreMarca?: string;            // default activaApi.nombreActual()
  compacto?: boolean;              // true: sólo la burbuja, sin el teléfono (lista de 3 ejemplos)
  vacio?: string;                  // texto cuando `texto` está vacío
  actualizando?: boolean;          // chip "actualizando…" arriba a la derecha
  /** Sólo `whatsapp/card`: el pie va aparte, en gris, debajo del cuerpo. */
  pie?: string;
  /** Sólo `whatsapp/card`: encabezado de texto en negrita arriba del cuerpo. */
  encabezadoTexto?: string;
}

/**
 * Markdown de WhatsApp: *negrita*, _cursiva_, ~tachado~, `código`. Copiado de WhatsAppPreview.tsx.
 * Exportado: el hilo de Conversaciones muestra cada mensaje igual que esta previa.
 */
export function renderWhatsAppMarkdown(text: string): ReactNode {
  const parts: ReactNode[] = [];
  const lines = text.split("\n");
  lines.forEach((line, lineIdx) => {
    let lastIndex = 0;
    const regex = /(\*([^*\n]+)\*|_([^_\n]+)_|~([^~\n]+)~|`([^`\n]+)`)/g;
    let match: RegExpExecArray | null;
    let key = 0;
    while ((match = regex.exec(line)) !== null) {
      if (match.index > lastIndex) parts.push(line.slice(lastIndex, match.index));
      if (match[2]) parts.push(<strong key={`${lineIdx}-${key++}`} className="font-semibold">{match[2]}</strong>);
      else if (match[3]) parts.push(<em key={`${lineIdx}-${key++}`} className="italic">{match[3]}</em>);
      else if (match[4]) parts.push(<span key={`${lineIdx}-${key++}`} className="line-through opacity-60">{match[4]}</span>);
      else if (match[5]) parts.push(<code key={`${lineIdx}-${key++}`} className="bg-black/5 px-1 rounded text-[12px] font-mono">{match[5]}</code>);
      lastIndex = match.index + match[0].length;
    }
    if (lastIndex < line.length) parts.push(line.slice(lastIndex));
    if (lineIdx < lines.length - 1) parts.push(<br key={`br-${lineIdx}`} />);
  });
  return parts;
}

function Imagen({ url }: { url: string }) {
  // El error se ata a la URL: si el operador corrige el link, se vuelve a intentar.
  const [fallo, setFallo] = useState<string | null>(null);
  if (fallo === url) {
    return (
      <div className="m-1.5 mb-0 rounded-md border border-dashed border-slate-300 bg-slate-50 px-3 py-4 text-center text-[11px] text-slate-500 flex flex-col items-center gap-1">
        <ImageOff size={16} className="text-slate-400" />
        No pudimos cargar la imagen. Tiene que ser una URL pública https.
      </div>
    );
  }
  return (
    <div className="p-1.5 pb-0">
      <img src={url} alt="Imagen de la plantilla" onError={() => setFallo(url)} className="w-full h-32 object-cover rounded-md" />
    </div>
  );
}

function Burbuja({ texto, mediaUrl, botones, vacio, pie, encabezadoTexto }: Pick<Props, "texto" | "mediaUrl" | "botones" | "vacio" | "pie" | "encabezadoTexto">) {
  const hora = new Date().toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" });
  const respuestas = (botones || []).filter((b) => b.tipo === "QUICK_REPLY");
  const acciones = (botones || []).filter((b) => b.tipo === "URL" || b.tipo === "PHONE_NUMBER");
  const sinTexto = !(texto || "").trim();

  return (
    <div className="flex">
      <div className="bg-white rounded-lg rounded-tl-none max-w-[88%] min-w-[60%] overflow-hidden" style={{ boxShadow: "0 1px 0.5px rgba(0,0,0,0.13)" }}>
        {mediaUrl && <Imagen url={mediaUrl} />}
        {encabezadoTexto && encabezadoTexto.trim() && (
          <div className="px-3 pt-2 pb-0">
            <p className="text-[13px] font-bold text-slate-900 leading-tight break-words">{encabezadoTexto}</p>
          </div>
        )}
        <div className="px-3 pt-2 pb-1">
          <div className={`text-[13px] leading-snug whitespace-pre-wrap break-words ${sinTexto ? "text-slate-400 italic" : "text-slate-900"}`}>
            {sinTexto ? (vacio || "Escribí el mensaje para ver cómo queda.") : renderWhatsAppMarkdown(texto)}
          </div>
        </div>
        {pie && pie.trim() && (
          <div className="px-3 pb-1">
            <p className="text-[11px] text-slate-400 leading-snug break-words">{pie}</p>
          </div>
        )}
        <div className="flex items-center justify-end gap-1 px-3 pb-1.5">
          <span className="text-[10px] text-slate-400">{hora}</span>
        </div>
        {/* Los botones son role="presentation": no hacen nada, y un <button> que no hace nada le
            promete una acción a un lector de pantalla. */}
        {[...respuestas, ...acciones].length > 0 && (
          <div className="border-t border-slate-100">
            {[...respuestas, ...acciones].map((b, i) => (
              <div key={`${b.tipo}-${b.id || b.url || b.telefono || ""}-${i}`}>
                {i > 0 && <div className="border-t border-slate-100" />}
                <div role="presentation" className="w-full py-2 px-2 text-[12px] font-medium text-[#00a884] flex items-center justify-center gap-1.5 text-center">
                  {b.tipo === "QUICK_REPLY" ? <Reply size={12} /> : b.tipo === "URL" ? <ExternalLink size={12} /> : <PhoneCall size={12} />}
                  <span className="truncate">{b.titulo || "(sin título)"}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function PreviaWhatsApp({ texto, mediaUrl, botones, nombreMarca, compacto = false, vacio, actualizando = false, pie, encabezadoTexto }: Props) {
  const marca = (nombreMarca || activaApi.nombreActual() || "Tu marca").trim();

  if (compacto) {
    return (
      <div className="relative rounded-xl bg-[#e5ddd5] p-2.5">
        {actualizando && <ChipActualizando />}
        <Burbuja texto={texto} mediaUrl={mediaUrl} botones={botones} vacio={vacio} pie={pie} encabezadoTexto={encabezadoTexto} />
      </div>
    );
  }

  return (
    <div className="relative bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
      <div className="flex items-center gap-2 mb-4">
        <MessageCircle size={16} className="text-emerald-500" />
        <h3 className="font-semibold text-slate-900 text-sm">Así lo ve el cliente</h3>
      </div>
      {actualizando && <ChipActualizando />}

      <div className="flex justify-center">
        <div className="w-[240px] rounded-[1.7rem] bg-gradient-to-b from-slate-900 to-slate-800 p-2 shadow-xl shadow-slate-900/20 relative">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-20 h-5 bg-black rounded-b-2xl z-20" />
          <div className="bg-[#e5ddd5] rounded-[1.3rem] overflow-hidden min-h-[360px] relative">
            <div className="bg-[#075E54] text-white px-2.5 pt-6 pb-1.5 flex items-center gap-2 relative z-10">
              <div className="w-7 h-7 rounded-full bg-emerald-500 flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0">
                {marca.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[11px] font-semibold leading-tight truncate">{marca}</p>
                <p className="text-[9px] text-white/70 leading-tight">cuenta de empresa</p>
              </div>
              <Video size={12} className="text-white/80" />
              <Phone size={11} className="text-white/80" />
              <MoreVertical size={12} className="text-white/80" />
            </div>

            <div className="px-2.5 py-3 relative z-10 min-h-[280px] flex flex-col justify-end">
              <div className="space-y-2">
                <div className="flex justify-center">
                  <span className="text-[10px] bg-white/80 text-slate-600 px-2.5 py-1 rounded-md shadow-sm">Hoy</span>
                </div>
                <Burbuja texto={texto} mediaUrl={mediaUrl} botones={botones} vacio={vacio} pie={pie} encabezadoTexto={encabezadoTexto} />
              </div>
            </div>

            <div className="absolute bottom-1.5 left-1/2 -translate-x-1/2 w-24 h-1 bg-black/20 rounded-full" />
          </div>
        </div>
      </div>
    </div>
  );
}

function ChipActualizando() {
  return (
    <span className="absolute top-3 right-3 z-20 inline-flex items-center gap-1 text-[10px] font-semibold text-slate-500 bg-white/90 border border-slate-200 rounded-full px-2 py-0.5">
      <Loader2 size={10} className="animate-spin" /> actualizando…
    </span>
  );
}

export default PreviaWhatsApp;
